// src/pages/Cameras/enhance_components/CameraThumbnail.jsx
// ✅ ENTERPRISE EDITION: Professional security camera thumbnails
// Shows: Last snapshot, metadata, status, recording indicator, quick actions

import React, { useState } from 'react';
import { 
  FiVideo, 
  FiMapPin, 
  FiClock, 
  FiActivity,
  FiAlertCircle,
  FiMaximize2,
  FiEye,
  FiCircle
} from 'react-icons/fi';
import { useTheme } from '../../../context/ThemeContext';

export default function CameraThumbnail({
  camera,
  onClick,
  onDragStart,
  draggable = false,
  isFirstLoad = false,
}) {
  const { theme } = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const [imageError, setImageError] = useState(false);

  // ✅ Status determination
  const isOnline = camera.connection_status === "online" || camera.is_online;
  const isInactive = camera.is_active === false;
  const isRecording = camera.is_recording !== false;

  // ✅ Last activity time
  const getLastActivity = () => {
    if (!camera.last_motion_at && !camera.updated_at) return null;
    
    const timestamp = new Date(camera.last_motion_at || camera.updated_at);
    const now = new Date();
    const diffMs = now - timestamp;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return `${Math.floor(diffMins / 1440)}d ago`;
  };

  const lastActivity = getLastActivity();

  // ✅ Try to get snapshot (implement backend endpoint: /api/cameras/{id}/snapshot)
  const snapshotUrl = camera.latest_snapshot_url || camera.snapshot_url;

  // ✅ Status badge configuration
  const statusConfig = isInactive ? {
    color: 'bg-gray-500',
    textColor: 'text-gray-200',
    label: 'Inactive',
    dotColor: 'bg-gray-300'
  } : isOnline ? {
    color: 'bg-green-500',
    textColor: 'text-white',
    label: 'Online',
    dotColor: 'bg-white'
  } : {
    color: 'bg-red-500',
    textColor: 'text-white',
    label: 'Offline',
    dotColor: 'bg-white/60'
  };

  return (
    <div
      onClick={onClick}
      onDragStart={onDragStart}
      draggable={draggable}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`group relative rounded-lg overflow-hidden border-2 transition-all duration-200 cursor-pointer ${
        theme === "dark"
          ? "bg-neutral-850 border-neutral-700 hover:border-blue-500"
          : "bg-white border-gray-200 hover:border-blue-500"
      } ${draggable ? 'cursor-move' : ''} hover:shadow-2xl hover:scale-[1.02]`}
    >
      {/* 📸 SNAPSHOT / POSTER FRAME */}
      <div className="relative aspect-video bg-black">
        {snapshotUrl && !imageError ? (
          <img
            src={snapshotUrl}
            alt={camera.name}
            onError={() => setImageError(true)}
            className="w-full h-full object-cover"
          />
        ) : (
          /* Fallback: Professional gradient */
          <div className={`w-full h-full flex flex-col items-center justify-center ${
            theme === "dark"
              ? "bg-gradient-to-br from-neutral-800 via-neutral-850 to-neutral-900"
              : "bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200"
          }`}>
            <FiVideo className={`w-16 h-16 mb-2 ${
              theme === "dark" ? "text-neutral-600" : "text-gray-400"
            }`} />
            <div className={`text-xs ${
              theme === "dark" ? "text-neutral-500" : "text-gray-500"
            }`}>
              No snapshot available
            </div>
          </div>
        )}

        {/* STATUS BADGE - Top Left */}
        <div className="absolute top-2 left-2 z-10">
          <div className={`${statusConfig.color} ${statusConfig.textColor} px-2.5 py-1 rounded-md text-xs font-bold flex items-center gap-1.5 shadow-lg backdrop-blur-sm`}>
            <div className={`w-2 h-2 rounded-full ${statusConfig.dotColor} ${isOnline && !isInactive ? 'animate-pulse' : ''}`} />
            {statusConfig.label}
          </div>
        </div>

        {/* RECORDING INDICATOR - Top Right */}
        {isRecording && isOnline && !isInactive && (
          <div className="absolute top-2 right-2 z-10">
            <div className="bg-red-600 text-white px-2.5 py-1 rounded-md text-xs font-bold flex items-center gap-1.5 shadow-lg backdrop-blur-sm">
              <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
              REC
            </div>
          </div>
        )}

        {/* HOVER OVERLAY */}
        <div
          className={`absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent transition-opacity duration-200 flex items-center justify-center ${
            isHovered ? "opacity-100" : "opacity-0"
          }`}
        >
          {/* QUICK ACTIONS */}
          <div className="flex items-center gap-3">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClick();
              }}
              className="p-4 rounded-full bg-blue-500 hover:bg-blue-600 text-white transition-all hover:scale-110 shadow-xl"
              title="View live stream"
            >
              <FiEye size={24} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                window.open(`/camera/${camera.id}`, '_blank');
              }}
              className="p-4 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-md text-white transition-all hover:scale-110 shadow-xl"
              title="Open in detail view"
            >
              <FiMaximize2 size={24} />
            </button>
          </div>

          {/* DRAG HINT */}
          {draggable && isFirstLoad && (
            <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 animate-bounce">
              <div className="bg-blue-500 text-white px-4 py-2 rounded-full text-xs font-bold shadow-2xl flex items-center gap-2">
                <FiCircle className="w-3 h-3 fill-current" />
                Click or drag to spotlight
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 📋 CAMERA INFO PANEL */}
      <div className={`p-3 ${
        theme === "dark" ? "bg-neutral-800" : "bg-gray-50"
      }`}>
        {/* CAMERA NAME */}
        <h3 className={`font-bold text-base mb-2 truncate ${
          theme === "dark" ? "text-white" : "text-gray-900"
        }`} title={camera.name}>
          {camera.name || "Unnamed Camera"}
        </h3>

        {/* METADATA */}
        <div className="space-y-1.5">
          {/* Location */}
          {camera.location && (
            <div className={`flex items-center gap-2 text-xs ${
              theme === "dark" ? "text-gray-400" : "text-gray-600"
            }`}>
              <FiMapPin className="w-3.5 h-3.5 flex-shrink-0 text-blue-500" />
              <span className="truncate font-medium">{camera.location}</span>
            </div>
          )}

          {/* Last Activity */}
          {lastActivity && (
            <div className={`flex items-center gap-2 text-xs ${
              theme === "dark" ? "text-gray-400" : "text-gray-600"
            }`}>
              <FiActivity className="w-3.5 h-3.5 flex-shrink-0 text-green-500" />
              <span>Last activity: <span className="font-medium">{lastActivity}</span></span>
            </div>
          )}

          {/* IP Address */}
          {camera.ip_address && (
            <div className={`flex items-center gap-2 text-xs ${
              theme === "dark" ? "text-gray-400" : "text-gray-600"
            }`}>
              <FiVideo className="w-3.5 h-3.5 flex-shrink-0 text-purple-500" />
              <code className="truncate font-mono text-xs">{camera.ip_address}</code>
            </div>
          )}
        </div>

        {/* BOTTOM BAR */}
        <div className={`mt-3 pt-2 border-t flex items-center justify-between text-xs ${
          theme === "dark" 
            ? "border-neutral-700 text-gray-500" 
            : "border-gray-200 text-gray-500"
        }`}>
          <span className="flex items-center gap-1.5">
            <FiClock className="w-3 h-3" />
            <span className="font-mono">ID: {camera.id}</span>
          </span>
          {camera.site_name && (
            <span className="truncate ml-2 font-medium">{camera.site_name}</span>
          )}
        </div>
      </div>

      {/* SELECTION GLOW */}
      {isHovered && (
        <div className="absolute inset-0 pointer-events-none border-4 border-blue-500 rounded-lg opacity-40" />
      )}
    </div>
  );
}
